import React, { useState, useEffect } from 'react';
import { 
  Tabs, 
  Button, 
  Space, 
  Spin,
  Alert,
  Typography,
  Tag
} from 'antd';
import { 
  InfoCircleOutlined,
  NodeIndexOutlined,
  ScheduleOutlined,
  ShopOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useTranslation } from 'react-i18next';

dayjs.extend(relativeTime);
import type { PlanogramTemplate } from '../../../types/plan-template';
import { PlanogramTemplateService } from '../../../services/planogramTemplateService';

// Import sub-components
import BasicInformation from './components/BasicInformation';
import MilestoneVisualization from './components/MilestoneVisualization';
import CycleConfiguration from './components/CycleConfiguration';
import ScopeConfiguration from './components/ScopeConfiguration';

const { Title } = Typography;
const { TabPane } = Tabs;

interface TemplateDetailProps {
  templateId: string;
  onEdit?: (template: PlanogramTemplate) => void;
  onDelete?: (templateId: string) => void;
  onBack?: () => void;
}

const TemplateDetail: React.FC<TemplateDetailProps> = ({
  templateId,
  onEdit,
  onDelete,
  onBack
}) => {
  const { t } = useTranslation();
  const [template, setTemplate] = useState<PlanogramTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('basic');

  const [wizardState, setWizardState] = useState<{
    visible: boolean;
    mode: 'create' | 'edit';
    templateId?: string;
  }>({
    visible: false,
    mode: 'create'
  });

  const handleOpenWizard = (mode: 'create' | 'edit', templateId?: string) => {
    setWizardState({
      visible: true,
      mode,
      templateId
    });
  };

  const handleCloseWizard = () => {
    setWizardState(prev => ({
      ...prev,
      visible: false
    }));
  };

  const handleWizardComplete = (template: PlanogramTemplate) => {
    console.log('Template saved:', template);
    handleCloseWizard();
    // 可以在这里添加成功提示或刷新数据的逻辑
  };

  useEffect(() => {
    loadTemplateDetail();
  }, [templateId]);

  const loadTemplateDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await PlanogramTemplateService.getTemplateById(templateId);
      
      if (response.success) {
        setTemplate(response.data);
      } else {
        setError(response.message || t('planTemplate.detail.loadFailed'));
      }
    } catch (err) {
      setError(t('planTemplate.detail.loadFailed'));
      console.error('Load template detail error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Published': return 'success';
      case 'Draft': return 'processing';
      case 'Disabled': return 'default';
      default: return 'default';
    };
  };

  const getStatusTranslation = (status: string) => {
    const statusKey = status.toLowerCase();
    return t(`planTemplate.status.${statusKey}`);
  };

  const renderTabContent = () => {
    if (!template) return null;

    switch (activeTab) {
      case 'basic':
        return (
          <BasicInformation 
            template={template}
            onEdit={onEdit}
            onDelete={onDelete}
            onReload={loadTemplateDetail}
          />
        );
      case 'milestone':
         return (
          <div style={{ paddingTop: '0', marginTop: '0px' }}>
            <MilestoneVisualization template={template} />
          </div>
         );
      case 'cycle':
        return <CycleConfiguration template={template} />;
      case 'scope':
        return <ScopeConfiguration template={template} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>{t('planTemplate.detail.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message={t('planTemplate.detail.loadFailed')}
        description={error}
        type="error"
        showIcon
        action={
          <Space>
            <Button size="small" onClick={loadTemplateDetail}>
              {t('planTemplate.detail.retry')}
            </Button>
            {onBack && (
              <Button size="small" onClick={onBack}>
                {t('planTemplate.detail.back')}
              </Button>
            )}
          </Space>
        }
      />
    );
  }

  if (!template) {
    return (
      <Alert
        message={t('planTemplate.detail.templateNotFound')}
        description={t('planTemplate.detail.templateNotFoundDesc')}
        type="warning"
        showIcon
        action={
          onBack && (
            <Button size="small" onClick={onBack}>
              {t('planTemplate.detail.back')}
            </Button>
          )
        }
      />
    );
  }

  return (
    <>
      {/* Fixed Header Section */}
      <div style={{ 
        position: 'sticky', 
        top: 0, 
        zIndex: 100, 
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #e8e8e8',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
        padding: '24px 24px 0 24px'
      }}>
        {/* Title and Actions */}
        <div style={{ marginBottom: 16 }}>
          <Space>
            {onBack && (
              <Button onClick={onBack}>
                {t('planTemplate.detail.back')}
              </Button>
            )}
            <Title level={2} style={{ margin: 0 }}>
              {template.name}
            </Title>
            <Tag color={getStatusColor(template.status)}>
              {getStatusTranslation(template.status)}
            </Tag>
          </Space>
        </div>

        {/* Fixed Tabs */}
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          type="card"
          size="large"
          style={{ marginBottom: 0 }}
        >
          <TabPane tab={
            <Space>
              <InfoCircleOutlined />
              {t('planTemplate.detail.tabs.basicInfo')}
            </Space>
          } key="basic" />
          <TabPane tab={
            <Space>
              <NodeIndexOutlined />
              {t('planTemplate.detail.tabs.milestoneConfig')}
            </Space>
          } key="milestone" />
          <TabPane tab={
            <Space>
              <ScheduleOutlined />
              {t('planTemplate.detail.tabs.executionCycle')}
            </Space>
          } key="cycle" />
          <TabPane tab={
            <Space>
              <ShopOutlined />
              {t('planTemplate.detail.tabs.scope')}
            </Space>
          } key="scope" />
        </Tabs>
      </div>

      {/* Scrollable Content Section */}
      <div style={{ 
        flex: 1, 
        overflow: 'auto', 
        padding: '24px',
        paddingTop: '16px',
        position: 'relative',
        zIndex: 1
      }}>
        {renderTabContent()}
      </div>
    </>
  );
};

export default TemplateDetail;